import I18n from 'I18n';
import { useEffect, useMemo } from 'react';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { useTheme } from 'foundations-theming/utils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { APP } from '../../constants/AuthType';
import { ADD_ON_CARD } from '../../constants/tracking/TrackingSources';
import { useTrackingFlow } from '../../hooks/useTrackingFlow';
import { useAppSelector } from '../../reducers';
import { checkoutProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasMaxProductQuantityInProductInventorySelector, hasProductVersionInProductInventorySelector, productsFromProductInventoryQuantitySelector, productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, includedQuantityBySkuIdSelector, isBreezeIntelligenceSkuSelector, isHubSpotCreditsSkuSelector, productForSkuIdSelector, productVersionSelector, unitNameForSkuIdSelector, upgradeProductSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector, talkToSalesTypeSelector } from '../../selectors/PurchaseMotionSelectors';
import { trackAdditionalUnitsPicker, trackBuyButtonClick, trackHoverInteraction, trackTalkToSalesClick } from '../../utils/tracking/trackingEventUtils';
import PurchaseMotionButtonWrapper from '../common/purchaseMotions/PurchaseMotionButtonWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AddOnCardCTA = ({
  productConfigurations,
  screen,
  selectedQuantity,
  skuId,
  term,
  trackingSource = ADD_ON_CARD,
  onQuantityChange
}) => {
  const theme = useTheme();
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const checkoutProductConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const ownedProduct = useAppSelector(state => productsFromProductInventorySelector(state)).find(inventoryProduct => inventoryProduct.currentSkuId === skuId);
  const ownedQuantity = useAppSelector(state => productsFromProductInventoryQuantitySelector(state, {
    skuId
  }));
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const quantifiable = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.quantifiable;
  });
  const ownsMaxQuantityOfProduct = useAppSelector(state => hasMaxProductQuantityInProductInventorySelector(state, {
    skuId
  }));
  const maxTouchlessPurchasableQuantity = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return productVersion.maxTouchlessPurchasableQuantity;
  });
  const ownsProduct = useAppSelector(state => hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const upgradeProduct = useAppSelector(state => upgradeProductSelector(state, {
    skuIds: checkoutProductConfigurations.map(config => config.skuId)
  }));
  const purchaseMotions = useAppSelector(state => {
    const motions = purchaseMotionsSelector(state, {
      skuId,
      location: 'CALCULATOR'
    });

    // we want to always fallback to showing something if there are no motions or
    // if the user owns the max quantity of the product, something is better than nothing
    if (motions.length === 0 || ownsMaxQuantityOfProduct) {
      return [talkToSalesTypeSelector(state)];
    }
    return motions;
  });
  const purchaseMotion = purchaseMotions[0];
  const isBreezeProduct = useAppSelector(state => isBreezeIntelligenceSkuSelector(state, {
    skuId
  }));
  const isHubSpotCreditsProduct = useAppSelector(state => isHubSpotCreditsSkuSelector(state, {
    skuId
  }));
  const unitName = useAppSelector(state => unitNameForSkuIdSelector(state, {
    skuId
  }));
  const includedUnits = useAppSelector(state => {
    var _includedQuantityBySk;
    return unitName ? (_includedQuantityBySk = includedQuantityBySkuIdSelector(state, {
      skuId,
      unitName
    })) !== null && _includedQuantityBySk !== void 0 ? _includedQuantityBySk : 1 : 1;
  });
  const isUpdate = (ownedProduct === null || ownedProduct === void 0 ? void 0 : ownedProduct.currentVersion.skuId) === skuId;
  const minValue = isUpdate ? includedUnits * ownedQuantity : includedUnits;
  const maxValue = maxTouchlessPurchasableQuantity === null ? Infinity : includedUnits * maxTouchlessPurchasableQuantity;
  const canBuyMultiple = quantifiable && maxValue > 1;
  const isTouchlessPurchase = purchaseMotion === BUY_TOUCHLESS;

  // set the quantity to the owned quantity if it's less than the owned quantity
  useEffect(() => {
    if (selectedQuantity < ownedQuantity) {
      onQuantityChange(ownedQuantity);
    }
  }, [ownedQuantity, onQuantityChange, selectedQuantity]);
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  const handleButtonClick = () => {
    if (isTouchlessPurchase) {
      trackBuyButtonClick({
        purchaseMotion: BUY_TOUCHLESS,
        productNames: product ? [product.apiName] : [],
        ownedProducts: ownsProduct && product ? [product.apiName] : [],
        flow,
        trackingSource
      });
    } else {
      trackTalkToSalesClick({
        trackingSource,
        productNames: product ? [product.apiName] : [],
        ownedProducts: ownsProduct && product ? [product.apiName] : [],
        flow
      });
    }
  };
  const handleStepperChange = evt => {
    const newQuantity = Math.round(parseInt(evt.target.value, 10) / includedUnits);
    if (isNaN(newQuantity)) return;
    const direction = newQuantity > selectedQuantity ? 'increase' : 'decrease';
    trackAdditionalUnitsPicker({
      trackingSource,
      action: `stepper ${direction}`,
      ownedUnitQuantity: ownedQuantity,
      displayedUnitQuantity: newQuantity * includedUnits,
      productName: product ? product.apiName : '',
      skuId,
      ownsProduct
    });
    onQuantityChange(newQuantity);
  };

  // disable the button if not touchless or the user hasn't increased the stepper to more than already owned
  const disabledButton = isTouchlessPurchase &&
  // only disable if touchless purchase is allowed (always let talk to sales be clickable)
  selectedQuantity <= ownedQuantity;
  const upgradeData = {
    apiName: product ? product.apiName : undefined,
    app: APP,
    screen,
    uniqueId: trackingSource,
    upgradeProduct
  };
  const disabledMinButtonTooltipKey = useMemo(() => {
    let productType = 'addOns';
    if (isBreezeProduct) {
      productType = 'breezeIntelligence';
    } else if (isHubSpotCreditsProduct) {
      productType = 'hubSpotCredits';
    }
    const tooltipType = isUpdate ? 'minTooltipExistingPlan' : 'minTooltip';
    return `pricingPage.pages.addOns.cards.${productType}.${tooltipType}`;
  }, [isHubSpotCreditsProduct, isBreezeProduct, isUpdate]);
  const textOverride = (isHubSpotCreditsProduct || isBreezeProduct) && ownedQuantity > 0 && isTouchlessPurchase ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "merchCards.breezeTile.buyAdditionalCredits"
  }) : undefined;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "200",
    children: [canBuyMultiple && /*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text(`pricing-pages.products.${billingNameKey}.title`),
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: theme.space[1600],
        children: /*#__PURE__*/_jsx(StepperInput, {
          "data-selenium-id": `${trackingSource}-${product === null || product === void 0 ? void 0 : product.apiName}-stepper-input`,
          disabled: ownsMaxQuantityOfProduct,
          disabledMinButtonTooltip: I18n.text(disabledMinButtonTooltipKey, {
            count: minValue
          }),
          disabledMinButtonTooltipProps: {
            onOpenChange: e => {
              if (e.currentTarget.value) {
                trackHoverInteraction({
                  value: getI18nText(disabledMinButtonTooltipKey, {
                    count: minValue,
                    locale: 'en-us'
                  }),
                  trackingSource
                });
              }
            },
            children: /*#__PURE__*/_jsx(_Fragment, {})
          },
          stepSize: includedUnits,
          onChange: handleStepperChange,
          value: selectedQuantity * includedUnits,
          min: minValue,
          max: maxValue
        })
      })
    }), /*#__PURE__*/_jsx(PurchaseMotionButtonWrapper, {
      queryParamTerm: term,
      use: "primary",
      trackingSource: trackingSource,
      upgradeData: upgradeData,
      purchaseMotion: purchaseMotion,
      productConfigurations: checkoutProductConfigurations,
      disabled: disabledButton,
      onClick: handleButtonClick,
      skipInterstitialPage: true,
      _textOverride: textOverride,
      purchaseMotionLocation: 'MERCH_CARD',
      children: textOverride
    })]
  });
};